import { useState } from 'react';
import { Badge, Button } from '@parsina/framework';

import { C } from '../../constants/theme';
import { ROW_PAD, STACK } from '../../constants/density';
import { useTranslation } from '../../i18n';

/**
 * The records already on one mobile number — the list every registration surface
 * shows before it creates a patient (ثبت بیمار جدید, «بیمار جدید» in ثبت نوبت, and
 * «تکمیل پرونده»).
 *
 * A mobile is a household, so this is a LIST and the desk picks: the person in
 * front of them is one of these rows, or someone new on the same number. Nothing
 * here decides for them.
 *
 * What a row may show follows what the clinic may see. A record already linked to
 * this clinic carries its name and شماره پرونده; any other record is a masked name,
 * a birth year and a gender — enough to recognise the person, and nothing to copy
 * into a form. That is why there is no «ویرایش و پیوند» any more.
 *
 * Actions:
 *   onChooseLinked(candidate)  a linked row — open it, or book for it. Without it
 *                              the row just says it is already the clinic's.
 *   onLink(candidate)          an unlinked row — attach it to this clinic. May throw;
 *                              the row reports the failure and stays actionable.
 *   onNewPatient()             «بیمار جدید با همین شماره».
 */
export default function PhoneCandidateList({
  candidates,
  onChooseLinked,
  chooseLabel,
  onLink,
  linkLabel,
  onNewPatient,
  'data-testid': testid = 'phone-candidates',
}) {
  const { t, formatDigits } = useTranslation();
  const [busyId, setBusyId] = useState(null);
  const [failedId, setFailedId] = useState(null);

  const link = async (c) => {
    setBusyId(c.patientId);
    setFailedId(null);
    try {
      await onLink(c);
    } catch {
      setFailedId(c.patientId);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div data-testid={testid} style={{ border: `1px solid ${C.border}`, borderRadius: 10, overflow: 'hidden' }}>
      {candidates.map((c, i) => {
        const gender = c.gender === 'male' ? t('common.gender.male')
          : c.gender === 'female' ? t('common.gender.female')
          : null;
        const meta = [
          c.birthYear ? t('patients.phoneCandidates.birthYear', { year: formatDigits(c.birthYear) }) : null,
          gender,
          c.isLinkedToThisClinic && c.fileNumber
            ? t('patients.phoneCandidates.fileNumber', { number: formatDigits(c.fileNumber) })
            : null,
        ].filter(Boolean);

        return (
          <div
            key={c.patientId}
            data-testid={`phone-candidate-${c.patientId}`}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: STACK.tight,
              padding: ROW_PAD.compact, borderTop: i > 0 ? `1px solid ${C.border}` : 'none',
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: STACK.tight }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: C.textPrimary }}>{c.displayName}</span>
                {c.isLinkedToThisClinic && <Badge variant="success">{t('patients.origin.inYourList')}</Badge>}
                {c.hasAppAccount && <Badge variant="outline">{t('patients.phoneCandidates.hasAppAccount')}</Badge>}
              </div>
              {meta.length > 0 && (
                // Separate spans, never a joined string: a «·» beside Persian digits reads
                // as a zero, and a literal separator would be copy outside the catalogues.
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: STACK.tight, fontSize: 11.5, color: C.textMuted, marginTop: 2 }}>
                  {meta.map(part => <span key={part}>{part}</span>)}
                </div>
              )}
              {failedId === c.patientId && (
                <div role="alert" style={{ fontSize: 11.5, color: C.red, fontWeight: 600, marginTop: 2 }}>
                  {t('patients.phoneCandidates.linkFailed')}
                </div>
              )}
            </div>

            {c.isLinkedToThisClinic ? (
              onChooseLinked ? (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onChooseLinked(c)}
                  data-testid={`phone-candidate-choose-${c.patientId}`}
                >
                  {chooseLabel || t('patients.phoneCandidates.alreadyInList')}
                </Button>
              ) : (
                <span style={{ fontSize: 12, fontWeight: 700, color: C.teal, whiteSpace: 'nowrap' }}>
                  {t('patients.phoneCandidates.alreadyInList')}
                </span>
              )
            ) : (
              <Button
                size="sm"
                onClick={() => link(c)}
                disabled={busyId !== null}
                data-testid={`phone-candidate-link-${c.patientId}`}
              >
                {busyId === c.patientId ? t('patients.phoneCandidates.adding') : (linkLabel || t('patients.phoneCandidates.addToList'))}
              </Button>
            )}
          </div>
        );
      })}

      {onNewPatient && (
        <div style={{ padding: ROW_PAD.compact, borderTop: `1px solid ${C.border}`, background: C.bg }}>
          <Button
            size="sm"
            variant="outline"
            onClick={onNewPatient}
            disabled={busyId !== null}
            data-testid="phone-candidates-new"
          >
            {t('patients.phoneCandidates.newOnSameNumber')}
          </Button>
        </div>
      )}
    </div>
  );
}
